import * as React from 'react';
import {NativeScrollEvent, NativeSyntheticEvent, RefreshControl, ScrollView, ScrollViewProps} from 'react-native';
import {useCallback, useState} from 'react';
import {sleep, throttle} from '../../pipes';
import {useDispatch, useSelector} from 'react-redux';
import {AppActions} from '../../../redux/actions';
import {useIsMounted} from '../../hooks/useIsMounted';
import {initialLoading} from '../../routing/navigation';
import {isInternetReachableSelector} from '../../../redux/selectors/httpWrapper/netInfo';
import {useAccountLinker} from '../../hooks/useAccountLinkerHook/useAccountLinker';

interface CustomScrollViewProps extends ScrollViewProps {
  children: React.ReactNode;
  withRefresh?: boolean;
  onRefresh?: () => void;
  onEndReached?: () => void;
  /**
   * How far from the end (in units of visible length of the list) the bottom edge of the
   * list must be from the end of the content to trigger the `onEndReached` callback.
   * Thus a value of 0.5 will trigger `onEndReached` when the end of the content is
   * within half the visible length of the list.
   */
  onEndReachedThreshold?: number;
}

const isEndReached = (
  {nativeEvent: {layoutMeasurement, contentOffset, contentSize}}: NativeSyntheticEvent<NativeScrollEvent>,
  onEndReachedThreshold: number,
) => {
  return layoutMeasurement.height + contentOffset.y >= contentSize.height * onEndReachedThreshold;
};

export const CustomScrollView = React.forwardRef<ScrollView, CustomScrollViewProps>((props, ref) => {
  const {onRefresh = undefined, withRefresh = true, onEndReached, onEndReachedThreshold = 0.8} = props;
  const isInternetReachable = useSelector(isInternetReachableSelector());
  const [refreshing, setRefreshing] = useState(false);
  const dispatch = useDispatch();
  const isMounted = useIsMounted();
  const refreshOnInternetReachable = React.useRef(false);
  const isFirstRender = React.useRef(true);

  const onRefreshLocal = useCallback(async () => {
    setRefreshing(true);
    dispatch({type: AppActions.Reset});
    await initialLoading(dispatch);
    if (!isInternetReachable) {
      refreshOnInternetReachable.current = true;
    }
    await sleep(300);
    if (isMounted.current) {
      setRefreshing(false);
    }
  }, [isInternetReachable]);

  React.useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      if (!isInternetReachable) {
        refreshOnInternetReachable.current = true;
      }
    }
  }, [isInternetReachable]);

  React.useEffect(() => {
    if (isInternetReachable && refreshOnInternetReachable.current) {
      refreshOnInternetReachable.current = false;
      (async () => {
        setRefreshing(true);
        dispatch({type: AppActions.Reset});
        await initialLoading(dispatch);
        await sleep(300);
        setRefreshing(false);
      })();
    }
  }, [isInternetReachable]);

  return (
    <ScrollView
      refreshControl={
        withRefresh ? (
          props.onRefresh ? (
            <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
          ) : (
            <RefreshControl refreshing={refreshing} onRefresh={onRefreshLocal} />
          )
        ) : undefined
      }
      keyboardShouldPersistTaps='handled'
      ref={ref}
      scrollEventThrottle={16}
      onScroll={
        onEndReached
          ? (event) => {
              if (isEndReached(event, onEndReachedThreshold)) {
                throttle(() => {
                  onEndReached();
                }, 1200);
              }
            }
          : undefined
      }
      {...props}
    />
  );
});
